import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { LanguageServiceService } from '../../services/language-service.service';
import { LoginService } from '../../components/Authentication/auth/login.service';
import { LogoutService } from '../../components/Authentication/auth/logout.service';
import { UserService } from '../../services/user';

@Component({
  selector: 'app-header-mobile',
  templateUrl: './header.mobile.component.html',
  styleUrl: './header.mobile.component.scss',
})
export class HeaderMobileComponent implements OnInit, AfterViewInit {
  @Input() isActive!: boolean;
  @ViewChild('navbarNav') navbarNav!: ElementRef;

  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  currentLanguage!: string | null;
  token!: string | null;
  tokenExists!: boolean;

  contentReady = true;
  showPopup = false;
  cartPopUp = false;
  showCart = true;
  openMenu = false;

  cartItems: any[] = [];
  cartItemCount = 0;

  accountType = '';
  isAdmin = false;
  isSubAdmin = false;
  isDeliveryHero = false;
  isEmployee = false;
  showAllUsers = false;

  lang = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'arabic' },
  ];

  constructor(
    private translate: TranslateService,
    private languageService: LanguageServiceService,
    private loginService: LoginService,
    private logoutService: LogoutService,
    private userService: UserService,
    private location: Location,
    private router: Router
  ) {
    translate.addLangs(['en', 'ar']);
    translate.setDefaultLang('en');

    if (this.isBrowser) {
      const savedLang = localStorage.getItem('language');
      const browserLang = translate.getBrowserLang();
      const langToUse =
        savedLang || (browserLang?.match(/en|ar/) ? browserLang : 'en');

      translate.use(langToUse);
      this.currentLanguage = langToUse;

      const path = this.location.path();
      this.showCart = !(path.includes('/Addorder') || path.includes('/Home'));
    }
  }

  // =========================
  // 🔁 Lifecycle
  // =========================
  ngOnInit(): void {
    if (!this.isBrowser) return;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.token = localStorage.getItem('token');
        this.loadCart();
        this.resolveUserType();
      }
    });

    this.languageService.currentLanguage$.subscribe((lang) => {
      this.currentLanguage = lang;
      this.translate.use(lang);
    });

    this.checkForToken();
    this.loadCart();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || !this.navbarNav) return;

    const navLinks =
      this.navbarNav.nativeElement.querySelectorAll('.nav-item');
    const navbarCollapse = document.querySelector('.navbar-collapse');

    navLinks.forEach((link: HTMLElement) => {
      link.addEventListener('click', () => {
        if (navbarCollapse?.classList.contains('show')) {
          const toggler = document.querySelector(
            '.navbar-toggler'
          ) as HTMLElement;
          toggler?.click();
        }
      });
    });
  }

  // =========================
  // 🌍 Language
  // =========================
  toggleLanguage() {
    if (!this.isBrowser) return;

    const newLang = this.currentLanguage === 'en' ? 'ar' : 'en';
    this.translate.use(newLang);
    this.languageService.changeLanguage(newLang);
    window.location.reload();
  }

  // =========================
  // 🔐 Auth
  // =========================
  checkForToken() {
    this.loginService.token$.subscribe((token) => {
      this.tokenExists = !!token;
      this.token = token;
    });
  }

  logout() {
    if (!this.isBrowser || !this.token) return;

    this.contentReady = true;
    this.logoutService.logout(this.token).subscribe({
      next: () => {
        localStorage.removeItem('cart');
        localStorage.removeItem('token');
        window.location.reload();
      },
      error: () => (this.contentReady = false),
    });
  }

  // =========================
  // 🛒 Cart
  // =========================
  loadCart() {
    if (!this.isBrowser) return;

    const storedCart = localStorage.getItem('cart');
    if (storedCart) {
      this.cartItems = JSON.parse(storedCart);
      this.cartItemCount = this.cartItems.length;
    } else {
      this.cartItems = [];
      this.cartItemCount = 0;
    }
  }

  removeFromCart(item: any) {
    if (!this.isBrowser) return;

    const index = this.cartItems.findIndex(
      (i) => i.id === item.id
    );
    if (index !== -1) {
      this.cartItems.splice(index, 1);
      localStorage.setItem('cart', JSON.stringify(this.cartItems));
      this.cartItemCount = this.cartItems.length;
    }

    if (this.cartItems.length === 0) {
      this.cartPopUp = false;
    }
  }

  cartPopUpOpen() {
    this.cartPopUp = true;
  }

  quickOrder() {
    this.router.navigate(['/Addorder'], {
      queryParams: { cart: JSON.stringify(this.cartItems) },
    });
  }

  // =========================
  // 👤 User Role
  // =========================
  resolveUserType() {
    this.userService.getAccountType().subscribe({
      next: (data) => {
        this.accountType = data['user_type'];

        this.isAdmin = this.accountType === '1';
        this.isSubAdmin = this.accountType === '6';
        this.isDeliveryHero = this.accountType === '8';
        this.isEmployee = this.accountType === '7';
        this.showAllUsers = this.isAdmin || this.isSubAdmin;
      },
      error: () => {
        localStorage.clear();
      },
    });
  }

  // =========================
  // ℹ️ UI Helpers
  // =========================
  openMenuToggle() {
    this.openMenu = !this.openMenu;
  }

  openPopup() {
    this.showPopup = true;
  }

  close() {
    this.openMenu = false;
    this.cartPopUp = false;
  }

  closePopup() {
    this.showPopup = false;
  }

  help() {
    this.openPopup();
  }
}
